import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { ScrollArea } from '@/components/ui/scroll-area'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Separator } from '@/components/ui/separator'
import { cn } from '@/lib/utils'
import type {
  LogicCondition,
  LogicConditionOperator,
  LogicDisplayTarget,
  LogicJump,
  Question,
  QuestionGroupReference,
} from '@/types/form'
import { ArrowRight, CheckCircle2, Plus, Split, Target, Trash2, Workflow } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { getQuestionTypeZhName } from './question-type-meta'

const operatorLabels: Record<LogicConditionOperator, string> = {
  equals: '等於',
  not_equals: '不等於',
  contains: '包含文字',
  not_contains: '不包含文字',
  starts_with: '開頭是',
  ends_with: '結尾是',
  greater_than: '大於',
  less_than: '小於',
  greater_or_equal: '大於或等於',
  less_or_equal: '小於或等於',
  is_answered: '已填答',
  is_not_answered: '未填答',
  includes: '包含選項',
  not_includes: '不包含選項',
  between: '介於',
}

const textOperators: LogicConditionOperator[] = [
  'equals',
  'not_equals',
  'contains',
  'not_contains',
  'starts_with',
  'ends_with',
  'is_answered',
  'is_not_answered',
]

const choiceOperators: LogicConditionOperator[] = [
  'equals',
  'not_equals',
  'includes',
  'not_includes',
  'is_answered',
  'is_not_answered',
]

const numberOperators: LogicConditionOperator[] = [
  'equals',
  'not_equals',
  'greater_than',
  'less_than',
  'greater_or_equal',
  'less_or_equal',
  'between',
  'is_answered',
  'is_not_answered',
]

function questionTypeLabel(question: Question) {
  return getQuestionTypeZhName(question.type)
}

function operatorsForQuestion(question?: Question): LogicConditionOperator[] {
  if (!question) return textOperators
  if (
    [
      'MULTIPLE_CHOICE',
      'DROPDOWN',
      'CHECKBOXES',
      'PICTURE_CHOICE',
      'RANKING',
      'NESTED_SELECT',
    ].includes(question.type)
  ) {
    return choiceOperators
  }
  if (
    ['RATING', 'NPS', 'OPINION_SCALE', 'DATE', 'TIME', 'NUMBER', 'NUMBER_SLIDER', 'TOTAL'].includes(
      question.type,
    )
  ) {
    return numberOperators
  }
  if (question.type === 'YES_NO') return ['equals', 'not_equals', 'is_answered', 'is_not_answered']
  return textOperators
}

function operatorNeedsValue(operator: LogicConditionOperator) {
  return operator !== 'is_answered' && operator !== 'is_not_answered'
}

function defaultCondition(field: string): LogicCondition {
  return { field, operator: 'equals', value: '' }
}

function hasConditionValue(condition: LogicCondition) {
  if (!operatorNeedsValue(condition.operator)) return true
  if (condition.operator === 'between') {
    return (
      Array.isArray(condition.value) &&
      condition.value.length >= 2 &&
      String(condition.value[0] ?? '').trim() !== '' &&
      String(condition.value[1] ?? '').trim() !== ''
    )
  }
  return String(condition.value ?? '').trim() !== ''
}

function questionLabel(question: Question, index: number) {
  return `${index + 1}. ${question.title}`
}

function groupOrderIndex(group: QuestionGroupReference) {
  const orderIndex = (group as unknown as { orderIndex?: unknown }).orderIndex
  return typeof orderIndex === 'number' ? orderIndex : Number.MAX_SAFE_INTEGER
}

function groupLabel(group: QuestionGroupReference) {
  return `題組：${group.title ?? group.id}`
}

function isQuestion(value: Question | undefined): value is Question {
  return Boolean(value)
}

function firstQuestionInGroup(group: QuestionGroupReference, questions: Question[]) {
  const questionById = new Map(questions.map((question) => [question.id, question]))
  const explicitQuestion = group.questionIds.map((id) => questionById.get(id)).find(isQuestion)
  if (explicitQuestion) return explicitQuestion
  return questions
    .filter((question) => question.groupId === group.id)
    .sort((a, b) => a.orderIndex - b.orderIndex)[0]
}

function getChoiceLabel(question: Question | undefined, value: unknown) {
  const choice = question?.choices.find((item) => item.value === value)
  return choice?.label ?? String(value ?? '')
}

function comparisonInputType(question?: Question) {
  if (question?.type === 'DATE') return 'date'
  if (question?.type === 'TIME') return 'time'
  if (
    ['RATING', 'NPS', 'OPINION_SCALE', 'NUMBER', 'NUMBER_SLIDER', 'TOTAL'].includes(
      question?.type ?? '',
    )
  )
    return 'number'
  return 'text'
}

function conditionSummary(condition: LogicCondition, questions: Question[]) {
  const question = questions.find((item) => item.id === condition.field)
  const fieldLabel = question?.title ?? condition.field
  if (!operatorNeedsValue(condition.operator)) {
    return `如果「${fieldLabel}」${operatorLabels[condition.operator]}`
  }
  if (condition.operator === 'between' && Array.isArray(condition.value)) {
    return `如果「${fieldLabel}」介於 ${condition.value[0] ?? ''} 到 ${condition.value[1] ?? ''}`
  }
  return `如果「${fieldLabel}」${operatorLabels[condition.operator]}「${getChoiceLabel(
    question,
    condition.value,
  )}」`
}

function normalizeConditionForField(
  condition: LogicCondition,
  question?: Question,
): LogicCondition {
  const operators = operatorsForQuestion(question)
  if (operators.includes(condition.operator)) return condition
  return { field: condition.field, operator: operators[0], value: '' }
}

function normalizeConditionForOperator(
  condition: LogicCondition,
  operator: LogicConditionOperator,
): LogicCondition {
  if (!operatorNeedsValue(operator)) return { ...condition, operator, value: undefined }
  if (operator === 'between') {
    const current = Array.isArray(condition.value) ? condition.value : ['', '']
    return { ...condition, operator, value: [String(current[0] ?? ''), String(current[1] ?? '')] }
  }
  return {
    ...condition,
    operator,
    value: Array.isArray(condition.value)
      ? String(condition.value[0] ?? '')
      : (condition.value ?? ''),
  }
}

function getTargetLabel(
  jump: Pick<LogicJump, 'targetQuestionId' | 'targetGroupId'>,
  questions: Question[],
  questionGroups: QuestionGroupReference[],
) {
  if (jump.targetGroupId) {
    const group = questionGroups.find((item) => item.id === jump.targetGroupId)
    return group ? groupLabel(group) : '題組不存在'
  }
  if (jump.targetQuestionId === null) return '結束問卷'
  return questions.find((question) => question.id === jump.targetQuestionId)?.title ?? '未選擇'
}

function getTargetSelectValue(jump: Pick<LogicJump, 'targetQuestionId' | 'targetGroupId'>) {
  if (jump.targetGroupId) return `group:${jump.targetGroupId}`
  if (jump.targetQuestionId === null) return 'end'
  return `question:${jump.targetQuestionId}`
}

function displayTargetKey(target: LogicDisplayTarget) {
  if (target.type === 'group') return `group:${target.groupId}`
  if (target.type === 'group_question')
    return `group_question:${target.groupId}:${target.questionId}`
  return `question:${target.questionId}`
}

function toQuestionDisplayTarget(question: Question): LogicDisplayTarget {
  return {
    type: 'question',
    id: `question:${question.id}`,
    questionId: question.id,
    groupId: question.groupId ?? null,
  }
}

function toGroupDisplayTarget(group: QuestionGroupReference): LogicDisplayTarget {
  return {
    type: 'group',
    id: `group:${group.id}`,
    groupId: group.id,
  }
}

function toGroupQuestionDisplayTarget(
  group: QuestionGroupReference,
  question: Question,
): LogicDisplayTarget {
  return {
    type: 'group_question',
    id: `group_question:${group.id}:${question.id}`,
    groupId: group.id,
    questionId: question.id,
  }
}

function groupQuestions(group: QuestionGroupReference, questions: Question[]) {
  const questionById = new Map(questions.map((question) => [question.id, question]))
  const explicitQuestions = group.questionIds.map((id) => questionById.get(id)).filter(isQuestion)
  if (explicitQuestions.length > 0) {
    return explicitQuestions.sort((left, right) => left.orderIndex - right.orderIndex)
  }
  return questions
    .filter((question) => question.groupId === group.id)
    .sort((left, right) => left.orderIndex - right.orderIndex)
}

function hasDisplayTarget(targets: LogicDisplayTarget[] | undefined, target: LogicDisplayTarget) {
  const key = displayTargetKey(target)
  return targets?.some((item) => displayTargetKey(item) === key) ?? false
}

function toggleDisplayTarget(
  targets: LogicDisplayTarget[] | undefined,
  target: LogicDisplayTarget,
) {
  const currentTargets = targets ?? []
  const key = displayTargetKey(target)
  if (currentTargets.some((item) => displayTargetKey(item) === key)) {
    return currentTargets.filter((item) => displayTargetKey(item) !== key)
  }
  return [...currentTargets, target]
}

function getDisplayTargetsLabel(
  targets: LogicDisplayTarget[] | undefined,
  questions: Question[],
  questionGroups: QuestionGroupReference[],
) {
  if (!targets || targets.length === 0) return '未設定指定顯示目標'
  const labels = targets.slice(0, 3).map((target) => {
    if (target.type === 'group') {
      return questionGroups.find((group) => group.id === target.groupId)?.title ?? target.groupId
    }
    return (
      questions.find((question) => question.id === target.questionId)?.title ?? target.questionId
    )
  })
  return `${labels.join('、')}${targets.length > 3 ? ` 等 ${targets.length} 項` : ''}`
}

function getDraftWarnings(
  jump: LogicJump,
  questions: Question[],
  currentQuestionId: string,
  questionGroups: QuestionGroupReference[],
) {
  const warnings: string[] = []
  const currentQuestion = questions.find((question) => question.id === currentQuestionId)
  const targetQuestion = jump.targetQuestionId
    ? questions.find((question) => question.id === jump.targetQuestionId)
    : null
  const targetGroup = jump.targetGroupId
    ? questionGroups.find((group) => group.id === jump.targetGroupId)
    : null
  const targetGroupFirstQuestion = targetGroup
    ? (firstQuestionInGroup(targetGroup, questions) ?? targetGroup.questions?.[0])
    : null

  if (jump.targetQuestionId && !targetQuestion) warnings.push('跳轉目標不存在，請重新選擇。')
  if (jump.targetGroupId && !targetGroup) warnings.push('跳轉題組不存在，請重新選擇。')
  if (jump.targetGroupId && targetGroup && !targetGroupFirstQuestion) {
    warnings.push('目標題組目前沒有可顯示題目，Player 會改往下一個有效題目。')
  }
  if (jump.targetQuestionId && jump.targetGroupId) {
    warnings.push('同一規則同時設定題目與題組目標，儲存時會以題組目標為準。')
  }
  if (
    jump.conditions.some(
      (condition) => !questions.some((question) => question.id === condition.field),
    )
  ) {
    warnings.push('有條件欄位不存在，請重新選擇判斷欄位。')
  }
  if (
    currentQuestion &&
    targetQuestion &&
    targetQuestion.orderIndex <= currentQuestion.orderIndex
  ) {
    warnings.push('目標題目在目前題之前或相同位置，可能造成循環跳題。')
  }
  if (
    currentQuestion &&
    targetGroupFirstQuestion &&
    targetGroupFirstQuestion.orderIndex <= currentQuestion.orderIndex
  ) {
    warnings.push('目標題組在目前題之前或相同位置，可能造成循環跳題。')
  }
  if (jump.conditions.some((condition) => !hasConditionValue(condition))) {
    warnings.push('有條件尚未填寫比對值。')
  }
  if (
    jump.displayTargets?.some((target) => {
      if (target.type === 'group') {
        return !questionGroups.some((group) => group.id === target.groupId)
      }
      return !questions.some((question) => question.id === target.questionId)
    })
  ) {
    warnings.push('有顯示目標不存在，請重新選擇指定顯示目標。')
  }
  return warnings
}

function ValueEditor({
  condition,
  question,
  onChange,
}: {
  condition: LogicCondition
  question?: Question
  onChange: (value: LogicCondition['value']) => void
}) {
  if (!operatorNeedsValue(condition.operator)) {
    return (
      <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-500">
        此條件不需要填值。
      </div>
    )
  }

  if (condition.operator === 'between') {
    const values = Array.isArray(condition.value) ? condition.value : ['', '']
    const inputType = comparisonInputType(question)
    return (
      <div className="grid grid-cols-2 gap-2">
        <Input
          value={String(values[0] ?? '')}
          onChange={(event) => onChange([event.target.value, String(values[1] ?? '')])}
          placeholder="最小值"
          type={inputType}
        />
        <Input
          value={String(values[1] ?? '')}
          onChange={(event) => onChange([String(values[0] ?? ''), event.target.value])}
          placeholder="最大值"
          type={inputType}
        />
      </div>
    )
  }

  if (question?.type === 'YES_NO') {
    return (
      <Select value={String(condition.value ?? 'true')} onValueChange={onChange}>
        <SelectTrigger>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="true">是</SelectItem>
          <SelectItem value="false">否</SelectItem>
        </SelectContent>
      </Select>
    )
  }

  if (question && question.choices.length > 0) {
    return (
      <Select value={String(condition.value ?? '')} onValueChange={onChange}>
        <SelectTrigger>
          <SelectValue placeholder="選擇答案值" />
        </SelectTrigger>
        <SelectContent>
          {question.choices.map((choice) => (
            <SelectItem key={choice.id} value={choice.value}>
              {choice.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    )
  }

  const inputType = comparisonInputType(question)
  const isNumeric = inputType === 'number'

  return (
    <Input
      value={String(condition.value ?? '')}
      onChange={(event) => onChange(isNumeric ? Number(event.target.value) : event.target.value)}
      placeholder={isNumeric ? '輸入數值' : '輸入比對文字'}
      type={inputType}
    />
  )
}

function DisplayTargetCheckbox({
  checked,
  label,
  description,
  onChange,
}: {
  checked: boolean
  label: string
  description?: string
  onChange: () => void
}) {
  return (
    <label className="flex cursor-pointer items-start gap-3 rounded-md border border-slate-200 bg-white px-3 py-2 text-sm transition-colors hover:border-indigo-200 hover:bg-indigo-50/40">
      <input
        type="checkbox"
        checked={checked}
        onChange={onChange}
        className="mt-1 size-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
      />
      <span className="min-w-0">
        <span className="block truncate font-medium text-slate-800">{label}</span>
        {description ? (
          <span className="mt-0.5 block truncate text-xs text-slate-500">{description}</span>
        ) : null}
      </span>
    </label>
  )
}

export function LogicRuleEditor({
  open,
  onOpenChange,
  jump,
  questions,
  questionGroups = [],
  currentQuestionId,
  onSave,
  onDelete,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  jump: LogicJump
  questions: Question[]
  questionGroups?: QuestionGroupReference[]
  currentQuestionId: string
  onSave: (jump: LogicJump) => void
  onDelete?: () => void
}) {
  const [draft, setDraft] = useState(jump)
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false)
  const currentQuestion = questions.find((question) => question.id === currentQuestionId)

  useEffect(() => {
    if (open) setDraft(structuredClone(jump))
  }, [jump, open])

  const conditionQuestions = useMemo(() => {
    const currentOrder = currentQuestion?.orderIndex ?? Number.MAX_SAFE_INTEGER
    const base = questions.filter((question) => question.orderIndex <= currentOrder)
    const extra = questions.filter((question) =>
      draft.conditions.some(
        (condition) =>
          condition.field === question.id && !base.some((item) => item.id === question.id),
      ),
    )
    return [...base, ...extra].sort((a, b) => a.orderIndex - b.orderIndex)
  }, [currentQuestion?.orderIndex, draft.conditions, questions])

  const targetQuestions = useMemo(() => {
    const currentOrder = currentQuestion?.orderIndex ?? -1
    const laterQuestions = questions.filter((question) => question.orderIndex > currentOrder)
    const selectedTarget = questions.find((question) => question.id === draft.targetQuestionId)
    if (selectedTarget && !laterQuestions.some((question) => question.id === selectedTarget.id)) {
      return [...laterQuestions, selectedTarget].sort((a, b) => a.orderIndex - b.orderIndex)
    }
    return laterQuestions
  }, [currentQuestion?.orderIndex, draft.targetQuestionId, questions])
  const targetGroups = useMemo(() => {
    const selectedTarget = questionGroups.find((group) => group.id === draft.targetGroupId)
    const groups = [...questionGroups]
    if (selectedTarget && !groups.some((group) => group.id === selectedTarget.id)) {
      groups.push(selectedTarget)
    }
    return groups.sort((left, right) => groupOrderIndex(left) - groupOrderIndex(right))
  }, [draft.targetGroupId, questionGroups])

  const canSave =
    draft.conditions.length > 0 &&
    draft.conditions.every((condition) => condition.field && hasConditionValue(condition))
  const draftWarnings = getDraftWarnings(draft, questions, currentQuestionId, questionGroups)
  const displayTargetsLabel = getDisplayTargetsLabel(
    draft.displayTargets,
    questions,
    questionGroups,
  )

  function updateCondition(index: number, patch: Partial<LogicCondition>) {
    setDraft((current) => ({
      ...current,
      conditions: current.conditions.map((condition, conditionIndex) => {
        if (conditionIndex !== index) return condition
        const next = { ...condition, ...patch }
        const question = questions.find((item) => item.id === next.field)
        return normalizeConditionForField(next, question)
      }),
    }))
  }

  function addCondition() {
    const field = currentQuestionId || conditionQuestions[0]?.id
    if (!field) return
    setDraft((current) => ({
      ...current,
      conditions: [...current.conditions, defaultCondition(field)],
    }))
  }

  function removeCondition(index: number) {
    setDraft((current) => ({
      ...current,
      conditions: current.conditions.filter((_, conditionIndex) => conditionIndex !== index),
    }))
  }

  function updateDisplayTarget(target: LogicDisplayTarget) {
    setDraft((current) => ({
      ...current,
      displayTargets: toggleDisplayTarget(current.displayTargets, target),
    }))
  }

  function saveDraft() {
    if (!canSave) return
    onSave({
      ...draft,
      targetQuestionId: draft.targetGroupId ? null : draft.targetQuestionId,
      targetGroupId: draft.targetGroupId ?? null,
      displayTargets: draft.displayTargets?.length ? draft.displayTargets : undefined,
      conditions: draft.conditions.map((condition) =>
        operatorNeedsValue(condition.operator) ? condition : { ...condition, value: undefined },
      ),
    })
    onOpenChange(false)
  }

  const targetLabel = getTargetLabel(draft, questions, questionGroups)

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="left-[10px] top-[10px] h-[calc(100dvh-20px)] max-h-none w-[calc(100dvw-20px)] max-w-none translate-x-0 translate-y-0 overflow-hidden p-0">
          <div className="flex h-full min-h-0 flex-col bg-slate-50">
            <div className="shrink-0 border-b border-slate-200 bg-white p-5 pr-14">
              <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                <DialogHeader>
                  <DialogTitle>邏輯規則設定</DialogTitle>
                  <DialogDescription>
                    設定此題送出後的判斷條件、規則順序與跳轉目標。排序越前的規則會優先判斷。
                  </DialogDescription>
                </DialogHeader>
                <div className="flex flex-wrap gap-2">
                  {onDelete && (
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setConfirmDeleteOpen(true)}
                    >
                      <Trash2 className="size-4" />
                      刪除規則
                    </Button>
                  )}
                  <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                    取消
                  </Button>
                  <Button type="button" disabled={!canSave} onClick={saveDraft}>
                    <CheckCircle2 className="size-4" />
                    儲存規則
                  </Button>
                </div>
              </div>
            </div>

            <ScrollArea className="min-h-0 flex-1">
              <div className="grid gap-5 p-5 xl:grid-cols-[minmax(0,1fr)_380px]">
                <div className="space-y-5">
                  <section className="rounded-lg border border-slate-200 bg-white p-5">
                    <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                      <div>
                        <h3 className="flex items-center gap-2 font-semibold text-slate-950">
                          <Workflow className="size-4 text-indigo-600" />
                          規則流程
                        </h3>
                        <p className="mt-1 text-sm text-slate-500">
                          從目前問題開始，符合條件後跳到指定題目或直接結束問卷。
                        </p>
                      </div>
                      <Badge variant="secondary">
                        目前題目：{currentQuestion?.title ?? '未選擇'}
                      </Badge>
                    </div>
                    <div className="mt-4 grid gap-3 md:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] md:items-center">
                      <div className="rounded-md border border-slate-200 bg-slate-50 p-4">
                        <div className="text-xs font-medium uppercase text-slate-500">From</div>
                        <div className="mt-1 font-medium text-slate-950">
                          {currentQuestion?.title ?? '未選擇題目'}
                        </div>
                      </div>
                      <ArrowRight className="hidden size-5 text-slate-400 md:block" />
                      <div className="rounded-md border border-indigo-200 bg-indigo-50 p-4">
                        <div className="text-xs font-medium uppercase text-indigo-600">Then</div>
                        <div className="mt-1 font-medium text-indigo-950">{targetLabel}</div>
                      </div>
                    </div>
                  </section>

                  <section className="rounded-lg border border-slate-200 bg-white p-5">
                    <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                      <div>
                        <h3 className="flex items-center gap-2 font-semibold text-slate-950">
                          <Split className="size-4 text-indigo-600" />
                          條件組
                        </h3>
                        <p className="mt-1 text-sm text-slate-500">
                          可使用目前題與前面題目的答案做判斷，多個條件可選擇全部符合或任一符合。
                        </p>
                      </div>
                      <div className="flex rounded-md border border-slate-200 bg-slate-50 p-1">
                        {(['AND', 'OR'] as const).map((operator) => (
                          <button
                            key={operator}
                            type="button"
                            onClick={() => setDraft((current) => ({ ...current, operator }))}
                            className={cn(
                              'rounded px-3 py-1.5 text-sm font-medium transition-colors',
                              draft.operator === operator
                                ? 'bg-white text-indigo-700 shadow-sm'
                                : 'text-slate-500 hover:text-slate-950',
                            )}
                          >
                            {operator === 'AND' ? '全部符合' : '任一符合'}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div className="mt-5 space-y-3">
                      {draft.conditions.map((condition, index) => {
                        const fieldQuestion = questions.find(
                          (question) => question.id === condition.field,
                        )
                        const operators = operatorsForQuestion(fieldQuestion)
                        return (
                          <div
                            key={`${condition.field}-${index}`}
                            className="rounded-lg border border-slate-200 bg-slate-50 p-4"
                          >
                            <div className="mb-3 flex items-center justify-between gap-3">
                              <div className="text-sm font-semibold text-slate-950">
                                條件 {index + 1}
                              </div>
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                disabled={draft.conditions.length <= 1}
                                onClick={() => removeCondition(index)}
                              >
                                <Trash2 className="size-4" />
                                移除
                              </Button>
                            </div>
                            <div className="grid gap-3 lg:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)_minmax(0,1.2fr)]">
                              <div className="space-y-2">
                                <Label>判斷欄位</Label>
                                <Select
                                  value={condition.field}
                                  onValueChange={(field) =>
                                    updateCondition(index, {
                                      field,
                                      value: '',
                                    })
                                  }
                                >
                                  <SelectTrigger>
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {conditionQuestions.map((question, questionIndex) => (
                                      <SelectItem key={question.id} value={question.id}>
                                        {questionLabel(question, questionIndex)}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                              <div className="space-y-2">
                                <Label>運算子</Label>
                                <Select
                                  value={condition.operator}
                                  onValueChange={(operator) =>
                                    updateCondition(
                                      index,
                                      normalizeConditionForOperator(
                                        condition,
                                        operator as LogicConditionOperator,
                                      ),
                                    )
                                  }
                                >
                                  <SelectTrigger>
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {operators.map((operator) => (
                                      <SelectItem key={operator} value={operator}>
                                        {operatorLabels[operator]}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                              <div className="space-y-2">
                                <Label>比對值</Label>
                                <ValueEditor
                                  condition={condition}
                                  question={fieldQuestion}
                                  onChange={(value) => updateCondition(index, { value })}
                                />
                              </div>
                            </div>
                            {!hasConditionValue(condition) && (
                              <div className="mt-3 rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-700">
                                請補上此條件的比對值。
                              </div>
                            )}
                          </div>
                        )
                      })}
                    </div>

                    <Button type="button" variant="outline" className="mt-4" onClick={addCondition}>
                      <Plus className="size-4" />
                      新增條件
                    </Button>
                  </section>

                  <section className="rounded-lg border border-slate-200 bg-white p-5">
                    <div className="mb-4">
                      <h3 className="flex items-center gap-2 font-semibold text-slate-950">
                        <Target className="size-4 text-indigo-600" />
                        跳轉目標
                      </h3>
                      <p className="mt-1 text-sm text-slate-500">
                        條件成立時前往後續題目；選擇結束問卷會直接顯示完成頁。
                      </p>
                    </div>
                    <div className="max-w-xl">
                      <Select
                        value={getTargetSelectValue(draft)}
                        onValueChange={(target) =>
                          setDraft((current) => {
                            if (target === 'end') {
                              return { ...current, targetQuestionId: null, targetGroupId: null }
                            }
                            if (target.startsWith('group:')) {
                              return {
                                ...current,
                                targetQuestionId: null,
                                targetGroupId: target.replace('group:', ''),
                              }
                            }
                            return {
                              ...current,
                              targetQuestionId: target.replace('question:', ''),
                              targetGroupId: null,
                            }
                          })
                        }
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="end">結束問卷</SelectItem>
                          {targetQuestions.map((question) => (
                            <SelectItem key={question.id} value={`question:${question.id}`}>
                              {question.orderIndex + 1}. {question.title}
                            </SelectItem>
                          ))}
                          {targetGroups.map((group) => (
                            <SelectItem key={group.id} value={`group:${group.id}`}>
                              {groupLabel(group)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </section>

                  <section className="rounded-lg border border-slate-200 bg-white p-5">
                    <div className="mb-4">
                      <h3 className="flex items-center gap-2 font-semibold text-slate-950">
                        <Target className="size-4 text-emerald-600" />
                        顯示指定目標
                      </h3>
                      <p className="mt-1 text-sm text-slate-500">
                        條件成立時額外顯示指定問卷問題、整個題組，或題組內的指定問題。未設定時只套用原本跳轉流程。
                      </p>
                    </div>
                    <div className="grid gap-4 xl:grid-cols-3">
                      <div className="min-w-0 rounded-md border border-slate-200 bg-slate-50 p-3">
                        <div className="mb-3 text-sm font-semibold text-slate-950">問卷問題</div>
                        <div className="max-h-72 space-y-2 overflow-auto pr-1">
                          {questions.map((question) => {
                            const target = toQuestionDisplayTarget(question)
                            return (
                              <DisplayTargetCheckbox
                                key={displayTargetKey(target)}
                                checked={hasDisplayTarget(draft.displayTargets, target)}
                                label={`${question.orderIndex + 1}. ${question.title}`}
                                description={
                                  question.groupId ? `題組：${question.groupId}` : undefined
                                }
                                onChange={() => updateDisplayTarget(target)}
                              />
                            )
                          })}
                        </div>
                      </div>

                      <div className="min-w-0 rounded-md border border-slate-200 bg-slate-50 p-3">
                        <div className="mb-3 text-sm font-semibold text-slate-950">題組</div>
                        <div className="max-h-72 space-y-2 overflow-auto pr-1">
                          {targetGroups.map((group) => {
                            const target = toGroupDisplayTarget(group)
                            return (
                              <DisplayTargetCheckbox
                                key={displayTargetKey(target)}
                                checked={hasDisplayTarget(draft.displayTargets, target)}
                                label={group.title ?? group.id}
                                description={`${groupQuestions(group, questions).length} 題`}
                                onChange={() => updateDisplayTarget(target)}
                              />
                            )
                          })}
                        </div>
                      </div>

                      <div className="min-w-0 rounded-md border border-slate-200 bg-slate-50 p-3">
                        <div className="mb-3 text-sm font-semibold text-slate-950">題組問題</div>
                        <div className="max-h-72 space-y-3 overflow-auto pr-1">
                          {targetGroups.map((group) => (
                            <div key={group.id} className="space-y-2">
                              <div className="truncate text-xs font-semibold text-slate-500">
                                {group.title ?? group.id}
                              </div>
                              {groupQuestions(group, questions).map((question) => {
                                const target = toGroupQuestionDisplayTarget(group, question)
                                return (
                                  <DisplayTargetCheckbox
                                    key={displayTargetKey(target)}
                                    checked={hasDisplayTarget(draft.displayTargets, target)}
                                    label={`${question.orderIndex + 1}. ${question.title}`}
                                    onChange={() => updateDisplayTarget(target)}
                                  />
                                )
                              })}
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </section>
                </div>

                <aside className="space-y-5 xl:sticky xl:top-5 xl:self-start">
                  <section className="rounded-lg border border-slate-200 bg-white p-5">
                    <h3 className="font-semibold text-slate-950">規則摘要</h3>
                    <div className="mt-4 space-y-3">
                      {draft.conditions.map((condition, index) => (
                        <div key={`${condition.field}-summary-${index}`} className="text-sm">
                          <Badge variant="secondary">
                            {draft.operator === 'AND' ? '且' : '或'}
                          </Badge>
                          <div className="mt-2 leading-6 text-slate-700">
                            {conditionSummary(condition, questions)}
                          </div>
                        </div>
                      ))}
                    </div>
                    <Separator className="my-4" />
                    <div className="rounded-md bg-indigo-50 p-3 text-sm leading-6 text-indigo-950">
                      條件成立後跳到：<span className="font-semibold">{targetLabel}</span>
                    </div>
                    <div className="mt-3 rounded-md bg-emerald-50 p-3 text-sm leading-6 text-emerald-950">
                      條件成立後顯示：<span className="font-semibold">{displayTargetsLabel}</span>
                    </div>
                  </section>

                  <section className="rounded-lg border border-slate-200 bg-white p-5">
                    <h3 className="font-semibold text-slate-950">規則診斷</h3>
                    <div className="mt-4 space-y-2">
                      {draftWarnings.map((warning) => (
                        <div
                          key={warning}
                          className="rounded-md bg-amber-50 px-3 py-2 text-sm leading-6 text-amber-800"
                        >
                          {warning}
                        </div>
                      ))}
                      {draftWarnings.length === 0 ? (
                        <div className="rounded-md bg-emerald-50 px-3 py-2 text-sm text-emerald-800">
                          目前沒有偵測到未設定目標、無效條件或循環風險。
                        </div>
                      ) : null}
                    </div>
                  </section>

                  <section className="rounded-lg border border-slate-200 bg-white p-5">
                    <h3 className="font-semibold text-slate-950">設計規劃</h3>
                    <div className="mt-4 space-y-3 text-sm leading-6 text-slate-600">
                      <p>1. 規則依右側面板排序由上往下判斷，第一個符合的規則會生效。</p>
                      <p>2. 條件欄位只建議使用目前題與前面題，避免跳轉時依賴尚未填答的答案。</p>
                      <p>3. 若沒有任何規則符合，填答者會依題目順序前往下一題。</p>
                      <p>4. 目標只建議選擇後續題或結束問卷，避免填答流程循環。</p>
                    </div>
                  </section>

                  <section className="rounded-lg border border-slate-200 bg-white p-5">
                    <h3 className="font-semibold text-slate-950">可用欄位</h3>
                    <div className="mt-4 space-y-2">
                      {conditionQuestions.map((question, index) => (
                        <div
                          key={question.id}
                          className="flex items-center justify-between gap-3 rounded-md border border-slate-200 px-3 py-2"
                        >
                          <span className="min-w-0 truncate text-sm">
                            {questionLabel(question, index)}
                          </span>
                          <Badge variant="secondary">{questionTypeLabel(question)}</Badge>
                        </div>
                      ))}
                    </div>
                  </section>
                </aside>
              </div>
            </ScrollArea>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={confirmDeleteOpen} onOpenChange={setConfirmDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>刪除此邏輯規則？</AlertDialogTitle>
            <AlertDialogDescription>
              刪除後，填答者將不再依此條件跳轉。此操作會立刻更新目前編輯中的問卷狀態。
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>取消</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                onDelete?.()
                setConfirmDeleteOpen(false)
                onOpenChange(false)
              }}
            >
              刪除規則
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
